import { useCallback } from "react"

import { Handle, Position, useReactFlow } from "@xyflow/react"

import type { NodeProps } from "@xyflow/react"

import { ShieldCheck, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { useNodeExecutionState } from "../../hooks/use-node-execution-state"
import { useWorkflowStore } from "../../stores/workflow-store"
import { NodeExecutionIndicator } from "../node-execution-indicator"
import { NodeOutputPopover } from "../node-output-popover"
import { NodeEditDialog } from "./node-edit-dialog"
import { ReferenceNodesPicker } from "./reference-nodes-picker"
import { TemplateField } from "./template-field"

/**
 * Fact-check node (#39): an LLM-as-judge verifies the incoming output (and any
 * collected sources) against an optional claim/criteria and emits a verdict
 * (PASS/FAIL + reasoning) a downstream conditional can branch on.
 */
export function FactCheckNode({ id, data, selected }: NodeProps) {
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData)
  const { deleteElements } = useReactFlow()
  const { isExecuting, isCompleted, output } = useNodeExecutionState(id)

  const handleDelete = useCallback(() => {
    void deleteElements({ nodes: [{ id }] })
  }, [id, deleteElements])

  const claim = (data.claim as string) || ""
  const criteria = (data.criteria as string) || ""
  const hasOutput = isCompleted && !!output
  const renderEditor = (isModal = false) => (
    <div className="grid gap-3">
      <div className="grid gap-1.5">
        <Label className="text-xs">Claim (optional)</Label>
        <TemplateField
          className="h-8 pr-8"
          placeholder="A specific claim to verify"
          value={claim}
          onChange={(value) => updateNodeData(id, { claim: value })}
        />
        <p className="text-muted-foreground text-[10px]">
          Optional. Leave blank to run a general completeness check of the incoming content instead
          of verifying a specific claim.
        </p>
      </div>
      <ReferenceNodesPicker
        currentNodeId={id}
        selectedNodeIds={(data.nodeReferences as string[] | undefined) ?? []}
        onChange={(ids) => updateNodeData(id, { nodeReferences: ids })}
      />
      <div className="grid gap-1.5">
        <Label className="text-xs">Criteria (optional)</Label>
        <TemplateField
          multiline
          className={cn("pr-8", isModal ? "min-h-[260px]" : "min-h-16")}
          placeholder="Extra checks the judge should apply"
          value={criteria}
          onChange={(value) => updateNodeData(id, { criteria: value })}
        />
        <p className="text-muted-foreground text-[10px]">
          Verifies the incoming text/sources. Outputs VERDICT / CONFIDENCE / NOTES.
        </p>
      </div>
    </div>
  )

  return (
    <NodeOutputPopover disabled={!hasOutput} output={output || ""}>
      <Card
        className={cn(
          "relative min-w-[240px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
          data.active ? "border-success ring-success ring-2" : "border-border",
          selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
          isExecuting && "workflow-node-executing",
          isCompleted && !isExecuting && "workflow-node-completed",
          hasOutput && "cursor-pointer"
        )}
      >
        <CardHeader className={cn("rounded-t-xl p-2", CATEGORY_TONES[1])}>
          <NodeExecutionIndicator
            hasOutput={hasOutput}
            isCompleted={isCompleted}
            isExecuting={isExecuting}
          />
          <CardTitle className="flex items-center justify-center gap-2 text-sm font-medium">
            <ShieldCheck className="size-4" />
            Fact-Check
            <NodeEditDialog
              description="Edit this fact-check node in a larger workspace."
              title="Fact-Check settings"
            >
              {renderEditor(true)}
            </NodeEditDialog>
          </CardTitle>
          {selected && (
            <Button
              className="nodrag absolute -top-2.5 -right-2.5 size-6 rounded-full p-0 shadow-md"
              size="icon"
              variant="destructive"
              onClick={handleDelete}
            >
              <X className="size-3" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-3">
          {renderEditor()}
          <Handle position={Position.Left} type="target" />
          <Handle position={Position.Right} type="source" />
        </CardContent>
      </Card>
    </NodeOutputPopover>
  )
}
